// SPDX-License-Identifier: MIT
export type RouteHandler = () => void | Promise<void>;
export type Routes = Record<string, RouteHandler>;

export interface Router {
  start(): void;
  navigate(path: string, opts?: { replace?: boolean }): void;
}

export function createRouter(routes: Routes): Router {
  let currentPath: string | null = null;

  function matchKey(url: string): string {
    // Strip query + hash for route-table lookup.
    return url.split(/[?#]/, 1)[0] ?? '/';
  }

  function run(url: string): void {
    const key = matchKey(url);
    const handler = routes[key];
    if (handler) {
      currentPath = key;
      void handler();
    } else {
      // Unknown path → redirect to / without a history entry
      history.replaceState(null, '', '/');
      const root = routes['/'];
      if (root) {
        currentPath = '/';
        void root();
      }
    }
  }

  function navigate(path: string, opts: { replace?: boolean } = {}): void {
    const key = matchKey(path);
    const samePath = window.location.pathname + window.location.search + window.location.hash === path;
    if (samePath && key === currentPath) return;
    if (opts.replace) {
      history.replaceState(null, '', path);
    } else {
      history.pushState(null, '', path);
    }
    run(path);
  }

  function start(): void {
    window.addEventListener('popstate', () => { run(window.location.pathname + window.location.search); });
    run(window.location.pathname + window.location.search);
  }

  return { start, navigate };
}
